import React from "react";
import { motion } from "framer-motion";
import {
  Service,
  Service1,
  Service2,
  Service3,
  Service4,
  Service5,
  Service6,
  Servicebg
} from "../assets";
import { FaSnowflake, FaTools, FaStore, FaCogs, FaWrench, FaCheck } from "react-icons/fa";

// Animation Variants
const container = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const slideFromLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0 },
};

const slideFromRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0 },
};

function Services() {
  const servicesTop = [
    {
      image: Service1,
      title: "Custom Engineering",
      description: "Use Salson Seals critical components to increase your system mean time between failures for your pumps, dryers, compressors, agitators, bead mills, boiler feed water systems, sanitary systems, potable or treated water systems.",
    },
    {
      image: Service2,
      title: "Onsite Support",
      description: "Our onsite service includes inspection service and retrofitting. This allows us to set up a service unit on our customer’s premises. The vehicles are equipped with the necessary range of seals, and are supported by qualified staff.",
    },
    {
      image: Service3,
      title: "Custom Designed Seals",
      description: "Salson custom builds extremely reliable mechanical seals that can dramatically increase the operating hours of your rotating equipment. Our top notch application engineering team designs mechanical seals to your requirements.",
    },
  ];

  const servicesBottom = [
    {
      image: Service4,
      title: "Mechanical Seal Repair",
      description: "We can repair any make or style of mechanical seal as well as rotary union seals. Our staff thoroughly inspect all repairable seals and will provide inspection reports with pictures when requested.",
    },
    {
      image: Service5,
      title: "Pump Repairing",
      description:"Our pump repair service ensures fast, efficient restoration of performance across all pump types. From diagnostics to complete rebuilds, our expert technicians deliver reliable service using quality parts to minimize downtime.",

    },
    {
      image: Service6,
      title: "Testing & Inspection",
      description: "We have in-house test bed at factory for running performance of each and every seals and pump. Every seals and pump is tested with hydraulic pressure for mechanical seal and pump, volute casing and casing cover.",
    },
  ];

  const leftItems = [
    { icon: <FaSnowflake className="text-blue-400" />, text: "CUSTOM ENGINEERING" },
    { icon: <FaStore className="text-black" />, text: "ONSITE SUPPORT" },
    { icon: <FaCogs className="text-black" />, text: "CUSTOM DESIGNED SEALS" },
  ];

  const rightItems = [
    { icon: <FaTools className="text-black" />, text: "MECHANICAL SEAL REPAIR" },
    { icon: <FaWrench  className="text-black" />, text: "PUMP REPAIRING" },
    { icon: <FaCheck className="text-black" />, text: "TESTING & INSPECTION" },
  ];

  return (
    <div className="bg-white text-gray-800">
      {/* Header Section */}
      <header
        className="relative h-64 md:h-64 bg-cover bg-center text-white flex flex-col items-center justify-center text-center px-6"
        style={{ backgroundImage: `url(${Servicebg})` }}
      >
        <div className="absolute inset-0 bg-blue-900 bg-opacity-70"></div>

        <motion.div
          className="relative z-10 max-w-3xl"
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h1>
          <p className="max-w-2xl mx-auto text-base md:text-lg">
            We believe in client satisfaction through best service.
          </p>
        </motion.div>
      </header>

      {/* --- Services Top (s1, s2, s3) --- */}
      <motion.div
  className="grid sm:grid-cols-1 md:grid-cols-3 gap-8 py-8 mb-4 px-4 md:px-6 max-w-screen-xl mx-auto"

        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {servicesTop.map((service, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
            className="bg-white rounded-2xl shadow-md hover:shadow-2xl hover:border-blue-400 hover:border-2 transition duration-300 text-center p-6"
          >
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-52 object-cover rounded-xl mb-4"
            />
            <h3 className="text-2xl font-semibold mb-2 text-gray-800">
              {service.title}
            </h3>
            <p className="text-gray-600 text-justify">{service.description}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Seal and Side Services */}
      <div className="flex flex-col items-center justify-center bg-gray-50 py-16 px-6 md:px-10 max-w-screen-xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-center gap-14 w-full">
          {/* Left Column */}
          <motion.div
            className="flex flex-col gap-10 items-center text-center"
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {leftItems.map((item, index) => (
              <motion.div
                key={index}
                variants={slideFromLeft}
                className="group flex flex-col items-center gap-2 text-xl font-semibold uppercase"
              >
                <span className="text-4xl">{item.icon}</span>
                <span className="text-lg md:text-xl text-gray-800 max-w-[200px] group-hover:text-blue-600 group-hover:underline transition duration-300">
                  {item.text}
                </span>
              </motion.div>
            ))}
          </motion.div>

          {/* Center Image */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            whileHover={{ scale: 1.05, rotate: 1 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="rounded-full border-[6px] border-blue-300 p-4 bg-white shadow-xl hover:shadow-blue-300 transition-shadow duration-300">
              <img
                src={Service}
                alt="Mechanical Seal"
                className="w-full max-w-[320px] object-contain"
              />
            </div>
          </motion.div>

          {/* Right Column */}
          <motion.div
            className="flex flex-col gap-10 items-center text-center"
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {rightItems.map((item, index) => (
              <motion.div
                key={index}
                variants={slideFromRight}
                className="group flex flex-col items-center gap-2 text-xl font-semibold uppercase"
              >
                <span className="text-4xl">{item.icon}</span>
                <span className="text-lg md:text-xl text-gray-800 max-w-[200px] group-hover:text-blue-600 group-hover:underline transition duration-300">
                  {item.text}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* --- Services Bottom (s4, s5, s6) --- */}
      <motion.div
        className="grid sm:grid-cols-1 md:grid-cols-3 gap-8 mb-12 py-12 px-4 md:px-6 max-w-screen-xl mx-auto"
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {servicesBottom.map((service, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
            className="bg-white rounded-2xl shadow-md hover:shadow-2xl hover:border-blue-400 hover:border-2 transition duration-300 text-center p-6"
          >
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-52 object-cover rounded-xl mb-4"
            />
            <h3 className="text-2xl font-semibold mb-2 text-gray-800">
              {service.title}
            </h3>
            <p className="text-gray-600 text-justify">{service.description}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

export default Services;
